import React, { useState, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { useNav } from "../../components/NavContext";
import { Line } from "react-chartjs-2";
import axios from "axios";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const DoctorMetrics = () => {
  const [mapVisits, setMapVisits] = useState([]);
  const [profileVisits, setProfileVisits] = useState([]);
  const [devicesCreated, setDevicesCreated] = useState([]);
  const [signedUpDevices, setSignedUpDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [days, setDays] = useState(60);
  const DOMAIN = process.env.REACT_APP_DOMAIN;
  const API_URL = process.env.REACT_APP_API_URL;

  const { addLink } = useNav();

  useEffect(() => {
    addLink("اطبائنا", `${DOMAIN}control/doctor-list`);
    addLink("Metrics", `${DOMAIN}control/admin/metrics`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addLink]);

  useEffect(() => {
    document.title = "Doctor Metrics";
  }, []);

  useEffect(() => {
    const fetchMetrics = async () => {
      setLoading(true);
      setError(null);

      try {
        const [doctorMetricsRes, deviceMetricsRes] = await Promise.all([
          axios.get(`${API_URL}admin/metrics/doctors`, { params: { days } }),
          axios.get(`${API_URL}admin/metrics/devices`, { params: { days } })
        ]);
        
        setMapVisits(doctorMetricsRes.data.map_visits || []);
        setProfileVisits(doctorMetricsRes.data.profile_visits || []);
        setDevicesCreated(deviceMetricsRes.data.devices_created || []);
        setSignedUpDevices(deviceMetricsRes.data.signed_up_devices || []);
      } catch (e) {
        console.error("Error fetching metrics:", e);
        setError("فشل تحميل البيانات");
      } finally {
        setLoading(false);
      }
    };

    fetchMetrics();
  }, [API_URL, days]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "100vh" }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  // Prepare chart data for map visits
  const mapChartData = {
    labels: mapVisits.map((item) => item.day),
    datasets: [
      {
        label: "Map Visits per Day",
        data: mapVisits.map((item) => item.total_requests),
        borderColor: "rgb(75, 192, 192)",
        backgroundColor: "rgba(75, 192, 192, 0.2)",
        tension: 0.1,
      },
    ],
  };

  // Prepare chart data for profile visits
  const profileChartData = {
    labels: profileVisits.map((item) => item.day),
    datasets: [
      {
        label: "Profile Visits per Day",
        data: profileVisits.map((item) => item.total_requests),
        borderColor: "rgb(255, 99, 132)",
        backgroundColor: "rgba(255, 99, 132, 0.2)",
        tension: 0.1,
      },
    ],
  };

  // Prepare chart data for devices created
  const devicesCreatedChartData = {
    labels: devicesCreated.map((item) => item.day),
    datasets: [
      {
        label: "Devices Created per Day",
        data: devicesCreated.map((item) => item.total_devices),
        borderColor: "rgb(54, 162, 235)",
        backgroundColor: "rgba(54, 162, 235, 0.2)",
        tension: 0.1,
      },
    ],
  };

  // Prepare chart data for signed up devices
  const signedUpDevicesChartData = {
    labels: signedUpDevices.map((item) => item.day),
    datasets: [
      {
        label: "Signed Up Devices per Day",
        data: signedUpDevices.map((item) => item.total_devices),
        borderColor: "rgb(153, 102, 255)",
        backgroundColor: "rgba(153, 102, 255, 0.2)",
        tension: 0.1,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          precision: 0,
        },
      },
    },
  };

  return (
    <div className="container mt-4" dir="rtl">
      <header className="mb-4 text-center">
        <h1 className="display-6 mb-1">Doctor Metrics</h1>
        <p className="text-muted mb-0">Last {days} Days</p>
      </header>

      {/* Days Selector */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card shadow-sm">
            <div className="card-body">
              <div className="row align-items-center">
                <div className="col-md-3">
                  <label htmlFor="daysSelect" className="form-label mb-0">
                    <strong>Number of Days:</strong>
                  </label>
                </div>
                <div className="col-md-9">
                  <select
                    id="daysSelect"
                    className="form-select"
                    value={days}
                    onChange={(e) => setDays(parseInt(e.target.value))}
                  >
                    <option value={7}>Last 7 Days</option>
                    <option value={14}>Last 14 Days</option>
                    <option value={30}>Last 30 Days</option>
                    <option value={60}>Last 60 Days</option>
                    <option value={90}>Last 90 Days</option>
                    <option value={180}>Last 180 Days</option>
                    <option value={365}>Last 365 Days</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      <div className="row g-4">
        {/* Map Visits Chart */}
        <div className="col-12 col-lg-6">
          <div className="card shadow-sm">
            <div className="card-header bg-info text-white">
              <h5 className="mb-0">Map Visits per Day</h5>
              <small>Endpoint: DoctorController@listNearbyDoctorsByField</small>
            </div>
            <div className="card-body">
              {mapVisits.length > 0 ? (
                <Line data={mapChartData} options={chartOptions} />
              ) : (
                <p className="text-muted text-center">No data available</p>
              )}
            </div>
          </div>
        </div>

        {/* Profile Visits Chart */}
        <div className="col-12 col-lg-6">
          <div className="card shadow-sm">
            <div className="card-header bg-danger text-white">
              <h5 className="mb-0">Profile Visits per Day</h5>
              <small>Endpoint: DoctorController@fetchDoctorByAlias</small>
            </div>
            <div className="card-body">
              {profileVisits.length > 0 ? (
                <Line data={profileChartData} options={chartOptions} />
              ) : (
                <p className="text-muted text-center">No data available</p>
              )}
            </div>
          </div>
        </div>

        {/* Devices Created Chart */}
        <div className="col-12 col-lg-6">
          <div className="card shadow-sm">
            <div className="card-header bg-primary text-white">
              <h5 className="mb-0">Devices Created per Day</h5>
              <small>Total user devices registered</small>
            </div>
            <div className="card-body">
              {devicesCreated.length > 0 ? (
                <Line data={devicesCreatedChartData} options={chartOptions} />
              ) : (
                <p className="text-muted text-center">No data available</p>
              )}
            </div>
          </div>
        </div>

        {/* Signed Up Devices Chart */}
        <div className="col-12 col-lg-6">
          <div className="card shadow-sm">
            <div className="card-header bg-purple text-white" style={{backgroundColor: "#9966ff"}}>
              <h5 className="mb-0">Signed Up Devices per Day</h5>
              <small>Devices with user authentication (sanctum_token_id)</small>
            </div>
            <div className="card-body">
              {signedUpDevices.length > 0 ? (
                <Line data={signedUpDevicesChartData} options={chartOptions} />
              ) : (
                <p className="text-muted text-center">No data available</p>
              )}
            </div>
          </div>
        </div>

        {/* Summary Statistics */}
        <div className="col-12">
          <div className="card shadow-sm">
            <div className="card-header bg-secondary text-white">
              <h5 className="mb-0">Summary Statistics</h5>
            </div>
            <div className="card-body">
              <div className="row text-center">
                <div className="col-md-3">
                  <h3 className="text-info">
                    {mapVisits.reduce((sum, item) => sum + parseInt(item.total_requests || 0), 0)}
                  </h3>
                  <p className="text-muted">Total Map Visits ({days} days)</p>
                </div>
                <div className="col-md-3">
                  <h3 className="text-danger">
                    {profileVisits.reduce((sum, item) => sum + parseInt(item.total_requests || 0), 0)}
                  </h3>
                  <p className="text-muted">Total Profile Visits ({days} days)</p>
                </div>
                <div className="col-md-3">
                  <h3 className="text-primary">
                    {devicesCreated.reduce((sum, item) => sum + parseInt(item.total_devices || 0), 0)}
                  </h3>
                  <p className="text-muted">Total Devices Created ({days} days)</p>
                </div>
                <div className="col-md-3">
                  <h3 style={{color: "#9966ff"}}>
                    {signedUpDevices.reduce((sum, item) => sum + parseInt(item.total_devices || 0), 0)}
                  </h3>
                  <p className="text-muted">Total Signed Up Devices ({days} days)</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DoctorMetrics;
